import type { MailPlanBulkOutput, MailPlanOp } from "@kwp/contracts";
import { useState } from "react";
import { Button } from "../../ui";
import { Dialog } from "../../ui/overlay";
import { BULK_OPS } from "./bulk-model";
import { PromptDialog } from "./dialogs";
import type { BulkTarget, useBulk } from "./use-bulk";

/**
 * The bar shown while rows are selected: how many, "select all matching", and the bulk actions.
 * Small selections act at once; bigger ones show their plan first (see `use-bulk.ts`).
 */
export function BulkBar({
  count,
  noun,
  allLoaded,
  matching,
  canMatch,
  target,
  suggest,
  bulk,
  onSelectMatching,
  onClear,
  first,
}: {
  count: number;
  noun: "conversation" | "message";
  /** Every loaded row is selected (offer "all matching"). */
  allLoaded: boolean;
  /** "All matching" was chosen. */
  matching: boolean;
  /** The view can be expressed as a query, so "all matching" is possible. */
  canMatch: boolean;
  target: BulkTarget;
  suggest: (picker: "tag" | "move") => string[];
  bulk: ReturnType<typeof useBulk>;
  onSelectMatching: () => void;
  onClear: () => void;
  /** Before the usual actions: the mailbox's way back to the inbox (Archived, Spam, Trash). */
  first?: { label: string; op: MailPlanOp } | undefined;
}) {
  const [prompt, setPrompt] = useState<"tag" | "move" | null>(null);
  const run = (op: MailPlanOp) => void bulk.run(target, op);
  const plural = count === 1 ? noun : `${noun}s`;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-2">
      <div role="toolbar" aria-label="Bulk actions" className="flex flex-wrap items-center gap-1">
        <span className="px-2 text-sm font-medium text-text" aria-live="polite">
          {matching ? `All ${plural} matching this view selected` : `${count} ${plural} selected`}
        </span>
        {first === undefined ? null : (
          <Button variant="ghost" disabled={bulk.pending} onClick={() => run(first.op)}>
            {first.label}
          </Button>
        )}
        {BULK_OPS.map((b) => (
          <Button
            key={b.key}
            variant="ghost"
           
            disabled={bulk.pending}
            onClick={() => (b.op === "tag" || b.op === "move" ? setPrompt(b.op) : run(b.op))}
          >
            {b.label}
          </Button>
        ))}
        <Button variant="ghost" onClick={onClear}>
          Clear selection
        </Button>
      </div>
      {allLoaded && canMatch && !matching ? (
        <p className="px-2 text-xs text-muted">
          All {count} loaded {plural} are selected.{" "}
          <button type="button" className="text-accent underline" onClick={onSelectMatching}>
            Select everything matching this view
          </button>
        </p>
      ) : null}
      {prompt === null ? null : (
        <PromptDialog
          title={prompt === "tag" ? "Tag the selection" : "Move the selection"}
          label={prompt === "tag" ? "Tag" : "Folder"}
          submitLabel={prompt === "tag" ? "Add tag" : "Move"}
          suggestions={suggest(prompt)}
          onSubmit={(name) => run(prompt === "tag" ? { kind: "addTags", tags: [name] } : { kind: "moveToFolder", folder: name })}
          onClose={() => setPrompt(null)}
        />
      )}
      {bulk.plan === null ? null : <PlanDialog plan={bulk.plan} pending={bulk.pending} onApply={() => void bulk.apply()} onClose={bulk.cancel} />}
    </div>
  );
}

const DAY = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });

/** A bulk plan before it runs (architecture §9 "Plans"): what it does, how many, a sample. */
export function PlanDialog({ plan, pending, onApply, onClose }: { plan: MailPlanBulkOutput; pending: boolean; onApply: () => void; onClose: () => void }) {
  const empty = plan.planId === null;
  return (
    <Dialog title="Review the bulk change" description={plan.summary} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text">
          {empty ? "Nothing matches, so there is nothing to change." : `${plan.matched} message${plan.matched === 1 ? "" : "s"} will change.`}
          {plan.capability === "destructive" && !empty ? " Spam and trash in bulk need your passkey; without one they wait for approval." : ""}
        </p>
        {plan.sample.length > 0 ? (
          <ul aria-label="Sample" className="flex flex-col gap-1 text-xs text-muted">
            {plan.sample.map((s, i) => (
              <li key={`${s.subject}-${i}`} className="truncate">
                {DAY.format(s.date)} · {s.from} · {s.subject === "" ? "(no subject)" : s.subject} <span className="text-muted">({s.account})</span>
              </li>
            ))}
          </ul>
        ) : null}
        {plan.warnings.length > 0 ? (
          <ul aria-label="Plan warnings" className="text-xs text-danger">
            {plan.warnings.map((w) => (
              <li key={`${w.accountId}-${w.reason}`}>
                {w.address} could not be searched ({w.reason}).
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          {empty ? null : (
            <Button variant="primary" disabled={pending} onClick={onApply}>
              {pending ? "Applying…" : `Apply to ${plan.matched}`}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
